import { useState } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import TripsPanel from "@/components/TripsPanel";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import LoginPage from "@/auth/LoginPage";
import AdminPanel from "@/auth/AdminPanel";
import TarifasPanel from "@/tarifas/TarifasPanel";
import ChangePasswordModal from "@/auth/ChangePasswordModal";
import { ArrowRight, LogOut, Loader2, KeyRound, ShieldCheck } from "lucide-react";

function Shell() {
  const [cpOpen, setCpOpen] = useState(false);
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const canTarifas = !!user?.permissions?.includes("tarifas");
  const forceChange = !!user?.must_change_password;
  const initialTab = new URLSearchParams(window.location.search).get("tab");
  const [tab, setTab] = useState(canTarifas && initialTab === "tarifas" ? "tarifas" : "trips");
  const activeTab = tab === "tarifas" && !canTarifas ? "trips" : tab;

  return (
    <div className="min-h-screen bg-background" data-testid="app-root">
      <header className="border-b border-border bg-card">
        <div className="mx-auto max-w-[1500px] px-6 py-5 flex items-end justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground font-medium">
              Espíritu Travel · Centro de integración
            </p>
            <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-foreground mt-1 flex items-center gap-3">
              SOFI
              <ArrowRight className="h-7 w-7 text-primary" strokeWidth={2.5} />
              KIMKIM
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:block text-right">
              <p className="text-sm font-semibold flex items-center justify-end gap-1.5" data-testid="header-user">
                {isAdmin && <ShieldCheck className="h-4 w-4 text-violet-600" />} {user?.name || user?.email}
              </p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
            <Button
              data-testid="open-change-password-button"
              variant="outline" size="sm"
              onClick={() => setCpOpen(true)}
              className="rounded-md"
            >
              <KeyRound className="h-4 w-4 md:mr-1" />
              <span className="hidden md:inline">Cambiar contraseña</span>
            </Button>
            <Button data-testid="logout-button" variant="outline" size="sm" onClick={logout} className="rounded-md">
              <LogOut className="h-4 w-4 md:mr-1" />
              <span className="hidden md:inline">Salir</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] px-6 py-8">
        <Tabs value={activeTab} onValueChange={setTab}>
          <TabsList className="rounded-md mb-6">
            <TabsTrigger value="trips" data-testid="tab-trips" className="rounded-md px-5">
              Viajes SOFI → kimkim
            </TabsTrigger>
            {canTarifas && (
              <TabsTrigger value="tarifas" data-testid="tab-tarifas" className="rounded-md px-5">
                Actualización tarifas
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="admin" data-testid="tab-admin" className="rounded-md px-5">
                Administración
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="trips">
            <TripsPanel />
          </TabsContent>

          {canTarifas && (
            <TabsContent value="tarifas">
              <TarifasPanel />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="admin">
              <AdminPanel />
            </TabsContent>
          )}
        </Tabs>
      </main>
      <Toaster position="top-center" richColors />
      <ChangePasswordModal open={cpOpen || forceChange} onOpenChange={setCpOpen} force={forceChange} />
    </div>
  );
}

function Gate() {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="auth-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!user) return <LoginPage />;
  return <Shell />;
}

export default function App() {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}
